import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { photoAPI, memberAPI } from '../services/api';
import { getUploadUrl } from '../utils/config';
import AppHeader from './AppHeader';
import PhotoViewer from './PhotoViewer';
import './Photos.css';

function Photos() {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [filterTag, setFilterTag] = useState('all');

  useEffect(() => {
    fetchPhotos();
    fetchMembers();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await photoAPI.getAll();
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
      alert('Failed to load photos');
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await memberAPI.getAll();
      setMembers(response.data.members || response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({ total: files.length, current: 0 });

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('photo', file);

      try {
        await photoAPI.upload(formData);
        setUploadProgress({ total: files.length, current: i + 1 });
      } catch (error) {
        console.error('Error uploading photo:', error);
        alert(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    setUploadProgress(null);
    e.target.value = ''; // Reset file input
    fetchPhotos(); // Reload photos
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await photoAPI.delete(photoId);
      setPhotos(photos.filter(p => p.id !== photoId));
      setSelectedPhoto(null);
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo');
    }
  };

  const handlePhotoClick = async (photo) => {
    try {
      const response = await photoAPI.getById(photo.id);
      setSelectedPhoto(response.data);
    } catch (error) {
      console.error('Error fetching photo details:', error);
      alert('Failed to load photo details');
    }
  };

  const handleCloseViewer = () => {
    setSelectedPhoto(null);
    fetchPhotos(); // Refresh to get updated tags
  };

  const filteredPhotos = photos.filter(photo => {
    if (filterTag === 'all') return true;
    if (filterTag === 'untagged') return !photo.tagged_people || photo.tagged_people.length === 0;
    return photo.tagged_people && photo.tagged_people.some(p => p.id === parseInt(filterTag));
  });

  const uniqueTags = [...new Set(photos.flatMap(p => p.tagged_people || []).map(t => t.id))];

  return (
    <div className="photos-page">
      <AppHeader />

      <div className="photos-content">
        <div className="photos-header">
          <h1>{t('photos.gallery', 'Photo Gallery')}</h1>
          <div className="header-actions">
            <label className="upload-btn">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                disabled={uploading}
                style={{ display: 'none' }}
              />
              {uploading ? (
                <span>{t('photos.uploading', 'Uploading')} {uploadProgress.current}/{uploadProgress.total}...</span>
              ) : (
                <span>+ {t('photos.upload', 'Upload Photos')}</span>
              )}
            </label>
          </div>
        </div>

        <div className="photos-filters">
          <label>{t('photos.filterBy', 'Filter by')}: </label>
          <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
            <option value="all">{t('photos.allPhotos', 'All Photos')} ({photos.length})</option>
            <option value="untagged">{t('photos.untagged', 'Untagged Photos')}</option>
            {uniqueTags.map(tagId => {
              const member = members.find(m => m.id === tagId);
              if (!member) return null;
              const count = photos.filter(p => p.tagged_people && p.tagged_people.some(t => t.id === tagId)).length;
              return (
                <option key={tagId} value={tagId}>
                  {member.first_name} {member.last_name} ({count})
                </option>
              );
            })}
          </select>
        </div>

        <div className="photos-grid">
          {filteredPhotos.map(photo => (
            <div
              key={photo.id}
              className="photo-card"
              onClick={() => handlePhotoClick(photo)}
            >
              <div className="photo-thumbnail">
                <img src={getUploadUrl(photo.file_path)} alt={photo.caption || 'Photo'} />
              </div>
              <div className="photo-info">
                {photo.photo_date && (
                  <div className="photo-date">
                    {new Date(photo.photo_date).toLocaleDateString()}
                  </div>
                )}
                {photo.caption && (
                  <div className="photo-caption">{photo.caption}</div>
                )}
                {photo.tagged_people && photo.tagged_people.length > 0 && (
                  <div className="photo-tags">
                    {photo.tagged_people.map((person, i) => (
                      <span key={i} className="tag-badge">
                        {person.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredPhotos.length === 0 && (
          <div className="empty-state">
            <p>
              {filterTag === 'all'
                ? t('photos.noPhotos', 'No photos uploaded yet. Click "Upload Photos" to get started!')
                : t('photos.noMatch', 'No photos match the selected filter.')}
            </p>
          </div>
        )}

        {selectedPhoto && (
          <PhotoViewer
            photo={selectedPhoto}
            members={members}
            onClose={handleCloseViewer}
            onDelete={() => handleDeletePhoto(selectedPhoto.id)}
          />
        )}
      </div>
    </div>
  );
}

export default Photos;
